import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { TimecardTemplateDto, TimecardTemplateSaveDto } from 'types/timecards';
import { ClassifierData } from 'types/classifiers';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';

type Option = { label: string; value: string };

/**
 * `DateInput` stores its value as a `yyyy-MM-dd` string, not a `Date` — which is already the wire
 * format, so the dates pass straight through in both directions. Lexicographic order on that format
 * is chronological order, hence the plain string comparison in the range check below.
 */
interface TimecardTemplateForm {
    matterType: Option | null;
    language: Option | null;
    templateText: string;
    validFrom: string | null;
    validUntil: string | null;
}

interface Props {
    initialData?: TimecardTemplateDto;
    onSubmit: (dto: TimecardTemplateSaveDto) => void;
    onClose: () => void;
}

const toOption = (classifier: ClassifierData | undefined, code: string): Option => ({
    // The case-type picker labels in English by contract, so a template stays identifiable to an
    // administrator whose own UI runs in another language.
    label: getClassifierText(classifier, code, undefined, 'eng'),
    value: code,
});

const TimecardTemplateEditModal = ({ initialData, onSubmit, onClose }: Props) => {
    // Read here rather than taken as a prop: the modal is handed to the modal stack as an element, so a
    // snapshot passed at open time would stay whatever it was — permanently empty when the classifier
    // query had not resolved yet. The query is shared and cached, so this costs no extra request.
    const { classifiers } = useClassifiers({ codes: ['LANGUAGE', 'MATTER_TYPE'] });
    const matterTypeClassifier = classifiers['MATTER_TYPE'];

    // Every case type, retired ones included: a template written for a type the firm has since stopped
    // offering must stay editable, and new ones may target a type that is not yet in its validity window.
    const matterTypeOptions = useMemo(
        () =>
            (matterTypeClassifier?.values ?? [])
                .map((value) => toOption(matterTypeClassifier, value.code))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [matterTypeClassifier]
    );
    const languageOptions = useMemo(
        () => getValidOptionsFromClassifier<string, string>(classifiers['LANGUAGE']) as Option[],
        [classifiers]
    );

    const { control, handleSubmit, setValue } = useForm<TimecardTemplateForm>({
        defaultValues: {
            matterType: initialData ? toOption(matterTypeClassifier, initialData.matterTypeCode) : null,
            language: initialData ? toOption(classifiers['LANGUAGE'], initialData.langCode) : null,
            templateText: initialData?.templateText ?? '',
            validFrom: initialData?.validFrom ?? null,
            validUntil: initialData?.validUntil ?? null,
        },
    });

    const onSave = (): void => {
        void handleSubmit((data) =>
            onSubmit({
                matterTypeCode: data.matterType?.value ?? '',
                langCode: data.language?.value ?? '',
                templateText: data.templateText,
                // Clearing the picker leaves null, but an unparseable entry leaves '': both mean "open".
                validFrom: data.validFrom || null,
                validUntil: data.validUntil || null,
            })
        )();
    };

    return (
        <>
            <ModalHeader title={t('configuration.timecard-templates')} onCancel={onClose} />
            <div className="h-full overflow-y-auto px-[23px] pt-[15px] pb-[45px]">
                <div className="flex flex-col gap-4">
                    <SelectInput
                        control={control}
                        setValue={setValue}
                        name="matterType"
                        label="matters.case-type"
                        placeholder="matters.select-case-type"
                        required
                        options={matterTypeOptions}
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                    />
                    <SelectInput
                        control={control}
                        setValue={setValue}
                        name="language"
                        label="configuration.timecard-template-language"
                        placeholder="configuration.timecard-template-select-language"
                        required
                        options={languageOptions}
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                    />
                    <TextAreaInput
                        control={control}
                        name="templateText"
                        required
                        label="configuration.timecard-template-text"
                    />
                    <div className="flex gap-4">
                        <DateInput
                            control={control}
                            setValue={setValue}
                            name="validFrom"
                            label="valid-from"
                            clearable
                            fullWidth
                        />
                        <DateInput
                            control={control}
                            setValue={setValue}
                            name="validUntil"
                            label="valid-to"
                            clearable
                            fullWidth
                            validate={(value, formValues) =>
                                !value ||
                                !formValues.validFrom ||
                                value >= formValues.validFrom ||
                                t('configuration.timecard-template-invalid-validity-range')
                            }
                        />
                    </div>
                </div>
            </div>
            <ModalFooter onCancel={onClose} onSave={onSave} />
        </>
    );
};

export default TimecardTemplateEditModal;
